import { useState } from 'react'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import type { AuroraState, UiState } from '../store/auroraStore'
import {
  activeChild,
  activeCycle as activeCycleSel,
  currentPlan,
  currentRubric,
  cycleProgress,
  planGoals,
  planPrinciples,
  planPriorities,
  planTitle,
  planWeeks,
  rubricDomains,
  type PlanGoal,
  type PlanWeek,
} from '../store/selectors'
import { formatAge, formatFriendlyDate, formatRelative, todayISO } from '../lib/dates'
import { domainColor, domainLabel } from '../lib/rubric'
import { DomainGrid } from '../components/rubric/DomainGrid'
import { DomainDetail } from '../components/rubric/DomainDetail'
import { SkillDetail } from '../components/rubric/SkillDetail'

/**
 * The child's own page: the picture of her, the plan built from it, and the
 * few settings that are hers rather than the app's.
 *
 * The rubric is a drill-down rather than three routes because the whole point
 * is that a number is a door — domain, skill, then the observations underneath
 * it — and a parent should be able to walk back out in one tap at every step.
 */

type Tab = 'rubric' | 'plan' | 'settings'

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'rubric', label: 'Rubric' },
  { key: 'plan', label: 'Plan' },
  { key: 'settings', label: 'Settings' },
]

export function ChildView() {
  const child = useAuroraStore(activeChild)
  const [tab, setTab] = useState<Tab>('rubric')
  const [domainKey, setDomainKey] = useState<string>()
  const [skillKey, setSkillKey] = useState<string>()

  if (!child) return null

  const open = (next: Tab) => {
    setTab(next)
    setDomainKey(undefined)
    setSkillKey(undefined)
  }

  return (
    <div className="page childview">
      <header className="page-head reveal" style={{ '--i': 0 } as React.CSSProperties}>
        <div className="page-eyebrow">{formatAge(child.birthDate, child.timezone) ?? 'Child'}</div>
        <h1 className="page-title">{child.name}</h1>
      </header>

      <div className="tabs" role="tablist" aria-label="Child">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            role="tab"
            id={`childtab-${key}`}
            aria-selected={tab === key}
            aria-controls={`childpanel-${key}`}
            className={`tab${tab === key ? ' is-active' : ''}`}
            onClick={() => open(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`childpanel-${tab}`} aria-labelledby={`childtab-${tab}`}>
        {tab === 'rubric' && (
          <RubricPanel
            childName={child.name}
            domainKey={domainKey}
            skillKey={skillKey}
            onOpenDomain={(key) => {
              setDomainKey(key)
              setSkillKey(undefined)
            }}
            onOpenSkill={(dKey, sKey) => {
              setDomainKey(dKey)
              setSkillKey(sKey)
            }}
            onBack={() => (skillKey ? setSkillKey(undefined) : setDomainKey(undefined))}
            onHome={() => {
              setDomainKey(undefined)
              setSkillKey(undefined)
            }}
          />
        )}
        {tab === 'plan' && <PlanPanel childName={child.name} timezone={child.timezone} />}
        {tab === 'settings' && <SettingsPanel child={child} />}
      </div>
    </div>
  )
}

// ── rubric ───────────────────────────────────────────────────

function RubricPanel({
  childName,
  domainKey,
  skillKey,
  onOpenDomain,
  onOpenSkill,
  onBack,
  onHome,
}: {
  childName: string
  domainKey?: string
  skillKey?: string
  onOpenDomain: (key: string) => void
  onOpenSkill: (domainKey: string, skillKey: string) => void
  onBack: () => void
  onHome: () => void
}) {
  const rubric = useAuroraStore(currentRubric) as any
  // The spine is the primary source; the rubric artifact is a legacy fallback.
  const observed = useAuroraStore((s: any) =>
    rubricDomains(s).reduce((n: number, d: any) => n + d.observedCount, 0),
  )
  const hydrated = useAuroraStore(isHydrated)

  if (!hydrated) return <GridSkeleton />

  if (!rubric && observed === 0) {
    return (
      <div className="empty section">
        <p className="empty-title">No picture yet</p>
        <p className="empty-body">
          Log a few moments and Aurora can put together where {childName} is right now — with the
          observations behind every part of it.
        </p>
        <div className="row row-2" style={{ justifyContent: 'center', marginTop: 'var(--s-4)' }}>
          <button
            className="btn btn-primary"
            onClick={() => ask(`Put together a picture of where ${childName} is right now`)}
          >
            Build a picture
          </button>
        </div>
      </div>
    )
  }

  if (domainKey) {
    return (
      <div className="section">
        <Crumbs
          onBack={onBack}
          trail={[
            { label: 'All areas', onClick: onHome },
            ...(skillKey
              ? [{ label: domainLabel(domainKey), onClick: () => onOpenDomain(domainKey) }]
              : []),
          ]}
          domainKey={domainKey}
        />
        {skillKey ? (
          <SkillDetail
            domainKey={domainKey}
            skillKey={skillKey}
            onOpenSkill={onOpenSkill}
            childName={childName}
          />
        ) : (
          <DomainDetail
            domainKey={domainKey}
            onOpenSkill={(key) => onOpenSkill(domainKey, key)}
            childName={childName}
          />
        )}
      </div>
    )
  }

  return (
    <section className="section">
      {rubric && (rubric.title || rubric.updatedAt) && (
        <div className="rubric-meta">
          {rubric.title && <p className="rubric-meta-title">{rubric.title}</p>}
          {rubric.summary && <p className="rubric-meta-summary">{rubric.summary}</p>}
          {rubric.updatedAt && (
            <p className="rubric-meta-when">Updated {formatRelative(rubric.updatedAt)}</p>
          )}
        </div>
      )}
      <DomainGrid onOpen={onOpenDomain} childName={childName} />
    </section>
  )
}

/**
 * Back sits first and is a real button, because on a phone the drill-down is
 * three levels deep and the browser's back gesture does not apply inside a tab.
 */
function Crumbs({
  onBack,
  trail,
  domainKey,
}: {
  onBack: () => void
  trail: Array<{ label: string; onClick: () => void }>
  domainKey?: string
}) {
  return (
    <nav
      className="crumbs"
      aria-label="Breadcrumb"
      style={{ '--domain-color': domainColor(domainKey, 'var(--accent)') } as React.CSSProperties}
    >
      <button className="crumbs-back" onClick={onBack}>
        <BackIcon />
        <span>Back</span>
      </button>
      <ol className="crumbs-trail">
        {trail.map((crumb) => (
          <li key={crumb.label}>
            <button className="crumbs-link" onClick={crumb.onClick}>
              {crumb.label}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  )
}

function GridSkeleton() {
  return (
    <section className="section" aria-busy="true" aria-label="Loading the rubric">
      <ul className="domaingrid">
        {Array.from({ length: 6 }, (_, i) => (
          <li key={i}>
            <div className="domaintile is-skeleton">
              <span className="skeleton" style={{ height: 13, width: '70%' }} />
              <span className="skeleton" style={{ height: 26, width: '35%' }} />
              <span className="skeleton" style={{ height: 6, width: '100%' }} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ── plan ─────────────────────────────────────────────────────

function PlanPanel({ childName, timezone }: { childName: string; timezone?: string }) {
  const plan = useAuroraStore(currentPlan) as any
  const cycle = useAuroraStore(activeCycleSel) as any
  const hydrated = useAuroraStore(isHydrated)
  const title = useAuroraStore(planTitle)
  const goals = useAuroraStore(planGoals)
  const priorities = useAuroraStore(planPriorities)
  const principles = useAuroraStore(planPrinciples)
  const weeks = useAuroraStore(planWeeks)
  const today = todayISO(timezone)
  const progress = useAuroraStore((s: AuroraState) => cycleProgress(s, today))

  if (!hydrated) {
    return (
      <section className="section" aria-busy="true" aria-label="Loading the plan">
        <div className="stack stack-3">
          {Array.from({ length: 3 }, (_, i) => (
            <span key={i} className="skeleton" style={{ height: 64 }} />
          ))}
        </div>
      </section>
    )
  }

  if ((!plan && !cycle) || weeks.length === 0) {
    return (
      <div className="empty section">
        <p className="empty-title">{plan || cycle ? 'The plan has no weeks yet' : 'No plan yet'}</p>
        <p className="empty-body">
          A cycle is six weeks of ten-minute blocks, built from what Aurora knows about{' '}
          {childName} right now. You can argue with any part of it.
        </p>
        <div className="row row-2" style={{ justifyContent: 'center', marginTop: 'var(--s-4)' }}>
          <button className="btn btn-primary" onClick={() => ask(`Plan the next cycle for ${childName}`)}>
            Plan a cycle
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="planpanel">
      <header className="planpanel-head section">
        <h2 className="planpanel-title">{title ?? plan?.title ?? 'The current cycle'}</h2>
        <p className="planpanel-progress tnum">
          Week {Math.max(1, progress.weeksElapsed)} of {progress.weeks} ·{' '}
          {progress.blocksSettled} of {progress.blocks} blocks done
        </p>
      </header>

      {goals.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h3 className="section-title">What this cycle is for</h3>
          </div>
          <ul className="goal-list">
            {goals.map((goal: PlanGoal) => (
              <li
                key={goal.statement}
                className="goal"
                style={{ '--domain-color': domainColor(goal.domain, 'var(--accent)') } as React.CSSProperties}
              >
                {goal.domain && <span className="pill pill-domain">{domainLabel(goal.domain)}</span>}
                <span className="goal-statement">{goal.statement}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {priorities.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h3 className="section-title">In priority order</h3>
          </div>
          <ol className="priority-list">
            {priorities.map((priority: string, i: number) => (
              <li key={priority} className="priority">
                <span className="priority-num tnum">{i + 1}</span>
                <span>{priority}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">The weeks</h3>
        </div>
        <ol className="week-list">
          {weeks.map((week: PlanWeek) => (
            <WeekRow key={week.number} week={week} today={today} />
          ))}
        </ol>
      </section>

      {principles.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h3 className="section-title">Rules this plan follows</h3>
          </div>
          <ul className="principle-list">
            {principles.map((principle: string) => (
              <li key={principle} className="principle-item">
                {principle}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="planpanel-foot">
        <button className="btn btn-ghost btn-sm" onClick={() => ask('Change something about this cycle')}>
          Change the plan
        </button>
      </div>
    </div>
  )
}

function WeekRow({ week, today }: { week: PlanWeek; today: string }) {
  const [open, setOpen] = useState(false)
  const current = week.days.some((d) => d.date === today)
  const blocks = week.days.reduce((sum, d) => sum + d.blocks.length, 0)

  return (
    <li className={`week${current ? ' is-current' : ''}`}>
      <button className="week-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="week-num tnum">{week.number}</span>
        <span className="week-text">
          <span className="week-theme">{week.theme ?? `Week ${week.number}`}</span>
          <span className="week-meta tnum">
            {week.days.length} days · {blocks} blocks
            {current ? ' · this week' : ''}
          </span>
        </span>
        <span className="week-chevron" aria-hidden="true">
          <ChevronIcon />
        </span>
      </button>

      {open && (
        <div className="week-body">
          {week.focus.length > 0 && (
            <ul className="week-focus">
              {week.focus.map((focus) => (
                <li
                  key={focus.statement}
                  style={{ '--domain-color': domainColor(focus.domain, 'var(--accent)') } as React.CSSProperties}
                >
                  <span className="pill pill-domain">{domainLabel(focus.domain)}</span>
                  {focus.statement}
                </li>
              ))}
            </ul>
          )}

          <ol className="week-days">
            {week.days.map((day) => (
              <li key={day.date} className={`week-day${day.date === today ? ' is-today' : ''}`}>
                <button
                  className="week-day-head"
                  onClick={() => {
                    const s = auroraStore.getState() as any
                    s.selectDate(day.date)
                    s.setView('today')
                  }}
                >
                  <span className="week-day-date">{formatFriendlyDate(day.date)}</span>
                  <span className="week-day-count tnum">
                    {day.blocks.length ? `${day.blocks.length} blocks` : 'Open day'}
                  </span>
                </button>
                {day.blocks.length > 0 && (
                  <ul className="week-blocks">
                    {day.blocks.map((block) => (
                      <li
                        key={block.id}
                        className={`week-block${block.status && block.status !== 'pending' ? ' is-settled' : ''}`}
                        style={{ '--domain-color': domainColor(block.domain, 'var(--accent)') } as React.CSSProperties}
                      >
                        <span className="week-block-dot" aria-hidden="true" />
                        {block.title}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </li>
  )
}

// ── settings ─────────────────────────────────────────────────

const THEMES: Array<{ key: UiState['theme']; label: string }> = [
  { key: 'light', label: 'Light' },
  { key: 'dark', label: 'Dark' },
  { key: 'system', label: 'System' },
]

function SettingsPanel({ child }: { child: any }) {
  const theme = useAuroraStore((s: AuroraState) => s.ui.theme)
  const setTheme = (next: UiState['theme']) => (auroraStore.getState() as any).setTheme(next)

  const facts: Array<{ label: string; value?: string }> = [
    { label: 'Name', value: child.name },
    { label: 'Age', value: formatAge(child.birthDate, child.timezone) },
    { label: 'Born', value: child.birthDate ? formatFriendlyDate(child.birthDate) : undefined },
    {
      label: 'Pronouns',
      value: child.pronouns
        ? `${child.pronouns.subject}/${child.pronouns.object}/${child.pronouns.possessive}`
        : undefined,
    },
    { label: 'Timezone', value: child.timezone },
  ]

  return (
    <div className="settings">
      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Her record</h3>
        </div>
        <dl className="factlist">
          {facts
            .filter((fact) => fact.value)
            .map((fact) => (
              <div key={fact.label} className="fact">
                <dt className="fact-label">{fact.label}</dt>
                <dd className="fact-value">{fact.value}</dd>
              </div>
            ))}
        </dl>
        <button
          className="btn btn-quiet btn-sm"
          onClick={() => ask(`Update ${child.name}'s details`)}
        >
          Change something
        </button>
      </section>

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Appearance</h3>
        </div>
        <div className="segmented" role="group" aria-label="Theme">
          {THEMES.map(({ key, label }) => (
            <button
              key={key}
              className={`segmented-item${theme === key ? ' is-active' : ''}`}
              aria-pressed={theme === key}
              onClick={() => setTheme(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </section>
    </div>
  )
}

// ── shared ───────────────────────────────────────────────────

/**
 * Before the first sync lands we do not know whether there is a rubric, and
 * "no picture yet" would be a lie rather than an empty state.
 */
function isHydrated(s: AuroraState): boolean {
  return Object.keys(s.artifacts).length > 0 || Object.keys(s.syncMeta).length > 0
}

function ask(text: string) {
  const s = auroraStore.getState() as any
  s.setComposing(text)
  s.setView('thread')
}

const S = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}
const BackIcon = () => (
  <svg {...S} width="15" height="15">
    <path d="M15 6l-6 6 6 6" />
  </svg>
)
const ChevronIcon = () => (
  <svg {...S} width="16" height="16">
    <path d="M6 9l6 6 6-6" />
  </svg>
)
